/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { capitalize, startCase } from "lodash";

/**
 * The modules the Audit Logs page can report on, in tab order.
 *
 * `module` is the API contract (session_logs?module_name=...) and must match the value the
 * backend workers write; `tabLabel` is what the operator sees. `displayName` overrides the
 * singular name used in the modal titles and is only needed where the stored module name reads
 * wrong -- it mirrors SessionLogs::ImportedModuleRegistry on the backend. `path` is where the
 * import modal's "View Details" sends the operator, and `narrowDialog` marks the modules whose
 * modal shows three columns or fewer.
 */
export const AUDIT_LOG_MODULES = [
  { value: "1", module: "employee", tabLabel: "Employees", path: "/admin/employees", narrowDialog: true },
  { value: "2", module: "job", tabLabel: "Jobs", path: "/admin/jobs", narrowDialog: true },
  { value: "3", module: "order", tabLabel: "Orders", path: "/admin/reports/homepage" },
  {
    value: "4",
    module: "employee_time_entries",
    tabLabel: "Employee Time Entries",
    path: "/admin/reports/employee_time_entries",
  },
  { value: "5", module: "configured_discount", tabLabel: "Configured Perqs", path: "/admin/configured_perqs" },
  { value: "6", module: "revenue_center", tabLabel: "Revenue Centers", path: "/admin/revenue_centers", narrowDialog: true },
  { value: "7", module: "sales_category", tabLabel: "Sales Categories", path: "/admin/sales_categories", narrowDialog: true },
  // Meal periods are stored as restaurant services; the rename is display-only, so the module
  // value stays 'restaurant_service' on the wire.
  {
    value: "8",
    module: "restaurant_service",
    tabLabel: "Meal Periods",
    displayName: "Meal Period",
    path: "/admin/meal_periods",
    narrowDialog: true,
  },
];

const findModule = (moduleName) => AUDIT_LOG_MODULES.find((item) => item.module === moduleName);

export const DEFAULT_AUDIT_LOG_MODULE = AUDIT_LOG_MODULES[0];

// Singular, operator-facing name for a module, used by the modal titles. Modules without an
// override keep the titleized module name they have always rendered.
export const auditModuleDisplayName = (moduleName) => (
  findModule(moduleName)?.displayName || startCase(capitalize(moduleName))
);

// Where the import modal's "View Details" sends the operator; null means there is nowhere to go,
// and the button is hidden rather than rendered as a no-op.
export const auditModuleNavigationPath = (moduleName) => findModule(moduleName)?.path || null;

export const auditModuleNarrowDialog = (moduleName) => Boolean(findModule(moduleName)?.narrowDialog);
